import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export type TradeAlertsTab = 'all' | 'hidden';
export type TradeAlertsFilter = 'all' | 'high-value';
export type TradeAlertsSort = 'newest' | 'oldest' | 'highest-value' | 'lowest-value';
export type TradeAlertsAlertStatus = 'all' | 'on' | 'off';

export interface TradeAlertCard {
  id: number;
  name: string;
  setCode: string;
  collectorNumber: string;
  imageUrl: string;
  foil: boolean;
  language: string; // lowercase Scryfall code (e.g. "en")
  condition: string; // e.g. "NEAR_MINT"
  scryfallId?: string;
}

export interface TradeAlertRequester {
  id: number;
  username: string;
  avatarUrl?: string;
}

export interface TradeAlert {
  id: string; // "{haveListItemId}-{wantListItemId}"
  haveListItemId: number;
  wantListItemId: number;
  card: TradeAlertCard;
  requester: TradeAlertRequester;
  pointValue: number;
  availableSince: string; // ISO date string
  hidden: boolean;
  /** Whether the matched Have item has SMS alerts enabled. */
  alertsEnabled: boolean;
}

export interface GetTradeAlertsRequest {
  tab?: TradeAlertsTab;
  filter?: TradeAlertsFilter;
  sort?: TradeAlertsSort;
  alertStatus?: TradeAlertsAlertStatus;
}

export interface TradeAlertsCounts {
  all: number;
  on: number;
  off: number;
}

export interface GetTradeAlertsResponse {
  alerts: TradeAlert[];
  allCount: number;
  hiddenCount: number;
  /**
   * Per-status counts for the three alert-status pills. Reflect the active tab +
   * high-value filters but are independent of the selected alertStatus.
   */
  counts: TradeAlertsCounts;
}

export interface HideTradeAlertRequest {
  haveListItemId: number;
  wantListItemId: number;
}

export interface HideTradeAlertResponse {
  alert: TradeAlert;
}

export const tradeAlertsApi = createApi({
  reducerPath: 'tradeAlertsApi',
  baseQuery: baseQuery,
  tagTypes: ['TradeAlerts'],
  endpoints: (builder) => ({
    getTradeAlerts: builder.query<GetTradeAlertsResponse, GetTradeAlertsRequest | void>({
      query: (params) => {
        const { tab = 'all', filter = 'all', sort = 'newest', alertStatus = 'on' } = params || {};
        const search = new URLSearchParams({ tab, filter, sort, alertStatus });
        return {
          url: `/trade-alerts?${search.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['TradeAlerts'],
    }),

    hideTradeAlert: builder.mutation<HideTradeAlertResponse, HideTradeAlertRequest>({
      query: (body) => ({
        url: '/trade-alerts/hide',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['TradeAlerts'],
    }),

    unhideTradeAlert: builder.mutation<HideTradeAlertResponse, HideTradeAlertRequest>({
      query: (body) => ({
        url: '/trade-alerts/unhide',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['TradeAlerts'],
    }),
  }),
});

export const {
  useGetTradeAlertsQuery,
  useHideTradeAlertMutation,
  useUnhideTradeAlertMutation,
} = tradeAlertsApi;
